import {DataTableColumn} from 'mantine-datatable';
import {NavLink} from 'react-router-dom';
import {I18n} from '@aws-amplify/core';
import {
    Text,
    Tooltip,
} from '@mantine/core';

import {formatTimestamp} from '../../../utils/TableTimestamps.tsx';
import {
    SyntheticUser,
    SyntheticUserState,
} from '../types.ts';
import {
    LifecycleStageBadge,
    NextWakeCell,
    SyntheticUserStateBadge,
} from '../profile/Badges.tsx';
import config from '../config';

// A tombstoned row must stay LEGIBLE (FNP-848) — it is dimmed slightly to show it
// is not live, never hidden and never rendered as an error state. Every column
// below has a defined rendering for a tombstone.
const isTombstoned = (item: SyntheticUser) => item.state === SyntheticUserState.TOMBSTONED;

const optional = (value?: string | null) => value ?? '—';

export const useSyntheticUsersTableColumns = () => {

    const columns: DataTableColumn<SyntheticUser>[] = [
        {
            accessor: 'email',
            sortable: true,
            title: I18n.get('Email'),
            width: '1fr',
            render: (item: SyntheticUser) => (
                <NavLink to={`${config.basePath}/${encodeURIComponent(item.id)}`}>
                    <Text size={'sm'} td={isTombstoned(item) ? 'line-through' : undefined}>
                        {item.email}
                    </Text>
                </NavLink>
            ),
        },
        {
            accessor: 'state',
            sortable: true,
            title: I18n.get('State'),
            width: '130px',
            render: (item: SyntheticUser) => <SyntheticUserStateBadge state={item.state}/>,
        },
        {
            accessor: 'lifecycleStage',
            sortable: true,
            title: I18n.get('Lifecycle'),
            width: '130px',
            render: (item: SyntheticUser) => <LifecycleStageBadge stage={item.lifecycleStage}/>,
        },
        {
            accessor: 'nextWakeAt',
            sortable: true,
            // THE operational field, and the default sort — placed early rather than
            // buried at the right edge.
            title: I18n.get('Next Wake'),
            width: '200px',
            render: (item: SyntheticUser) => <NextWakeCell record={item}/>,
        },
        {
            accessor: 'personaId',
            sortable: true,
            title: I18n.get('Persona'),
            width: '1fr',
            render: (item: SyntheticUser) => (
                <NavLink to={`/testing-personas/${encodeURIComponent(item.personaId)}`}>
                    <Text size={'xs'} ff={'monospace'}>{item.personaId}</Text>
                </NavLink>
            ),
        },
        {
            accessor: 'bornAt',
            sortable: true,
            title: I18n.get('Born'),
            width: '190px',
            render: (item: SyntheticUser) => (
                <Text size={'sm'} c={isTombstoned(item) ? 'dimmed' : undefined}>
                    {formatTimestamp(item.bornAt)}
                </Text>
            ),
        },
        {
            accessor: 'accountId',
            sortable: true,
            title: I18n.get('Account'),
            width: '1fr',
            render: (item: SyntheticUser) => {
                // A tombstone's account is GONE — that is the definition. Say so
                // rather than showing an em dash that reads as missing data.
                if (!item.accountId && isTombstoned(item)) {
                    return (
                        <Tooltip label={'The account was deleted; the synthetic user row survives it.'} withArrow>
                            <Text size={'xs'} c={'dimmed'} fs={'italic'}>account deleted</Text>
                        </Tooltip>
                    );
                }
                return <Text size={'xs'} ff={'monospace'} c={'dimmed'}>{optional(item.accountId)}</Text>;
            },
        },
        {
            accessor: 'cognitoSub',
            sortable: true,
            title: I18n.get('Cognito Sub'),
            width: '1fr',
            render: (item: SyntheticUser) => (
                <Text size={'xs'} ff={'monospace'} c={'dimmed'}>{optional(item.cognitoSub)}</Text>
            ),
        },
        // NOTE: there is deliberately NO secretArn column, and no action that
        // resolves one. The row carries a credential reference, not a credential.
    ];

    return {
        columns,
    };
};
